import { Link as RouterLink } from 'react-router-dom';
import {
  VStack,
  Flex,
  Text,
  Icon,
  IconButton,
  Tooltip,
  Link,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Spacer,
  Portal,
  useColorMode,
} from '@chakra-ui/react';
import {
  FilePlus,
  Upload,
  Link as LinkIcon,
  Plus,
  ChevronLeft,
  RefreshCw,
  Settings,
  Sun,
  Moon,
} from 'lucide-react';

interface SidebarHeaderProps {
  onToggleCollapse: () => void;
  onNewFile: () => void;
  handleFileUpload: () => void;
  onUrlOpen: () => void;
  onSettings?: () => void;
  hasApiKey: boolean;
  isLoading: boolean;
  loadShares: () => void;
}

export function SidebarHeader({
  onToggleCollapse,
  onNewFile,
  handleFileUpload,
  onUrlOpen,
  onSettings,
  hasApiKey,
  isLoading,
  loadShares,
}: SidebarHeaderProps) {
  const { colorMode, toggleColorMode } = useColorMode();

  return (
    <VStack spacing={0} w="full" flexShrink={0}>
      {/* Logo row */}
      <Flex
        align="center"
        justify="space-between"
        px={3}
        h="45px"
        minH="45px"
        maxH="45px"
        borderBottomWidth="1px"
        borderColor={colorMode === 'dark' ? 'stone.700' : 'stone.200'}
        bg={colorMode === 'dark' ? '#0c0a09' : 'white'}
        w="full"
      >
        {/* Logo */}
        <Link
          as={RouterLink}
          to="/"
          _hover={{ textDecoration: 'none' }}
        >
          <Text
            fontFamily="heading"
            fontWeight="bold"
            fontSize="lg"
            color={colorMode === 'dark' ? 'white' : 'stone.800'}
          >
            myrkup
          </Text>
        </Link>

        {/* Collapse button */}
        <Tooltip label="Collapse sidebar">
          <IconButton
            aria-label="Collapse sidebar"
            icon={<Icon as={ChevronLeft} boxSize={4} />}
            variant="ghost"
            size="sm"
            onClick={onToggleCollapse}
          />
        </Tooltip>
      </Flex>

      {/* File actions row - aligns with MDEditor toolbar */}
      <Flex
        align="center"
        justify="flex-start"
        px={2}
        h="44px"
        minH="44px"
        maxH="44px"
        borderBottomWidth="1px"
        borderColor={colorMode === 'dark' ? 'stone.700' : 'stone.200'}
        bg={colorMode === 'dark' ? '#0c0a09' : 'white'}
        w="full"
      >
        <Menu>
          <Tooltip label="New">
            <MenuButton
              as={IconButton}
              aria-label="New"
              icon={<Icon as={Plus} boxSize={5} />}
              variant="ghost"
              size="sm"
            />
          </Tooltip>
          <Portal>
            <MenuList>
              <MenuItem
                icon={<Icon as={FilePlus} boxSize={4} />}
                onClick={onNewFile}
              >
                New file
              </MenuItem>
              <MenuItem
                icon={<Icon as={Upload} boxSize={4} />}
                onClick={handleFileUpload}
              >
                From file
              </MenuItem>
              <MenuItem
                icon={<Icon as={LinkIcon} boxSize={4} />}
                onClick={onUrlOpen}
              >
                From URL
              </MenuItem>
            </MenuList>
          </Portal>
        </Menu>
        <Spacer />
        {/* Theme toggle */}
        <Tooltip
          label={colorMode === 'dark' ? 'Light mode' : 'Dark mode'}
        >
          <IconButton
            aria-label={
              colorMode === 'dark' ? 'Light mode' : 'Dark mode'
            }
            icon={
              <Icon
                as={colorMode === 'dark' ? Sun : Moon}
                boxSize={5}
              />
            }
            variant="ghost"
            size="sm"
            onClick={toggleColorMode}
          />
        </Tooltip>
        {/* Global settings button */}
        {onSettings && (
          <Tooltip label="Share settings defaults">
            <IconButton
              aria-label="Share settings defaults"
              icon={<Icon as={Settings} boxSize={5} />}
              variant="ghost"
              size="sm"
              colorScheme="brand"
              onClick={onSettings}
            />
          </Tooltip>
        )}
        {/* Refresh published shares */}
        {hasApiKey && (
          <Tooltip label="Refresh published">
            <IconButton
              aria-label="Refresh published"
              icon={<Icon as={RefreshCw} boxSize={5} />}
              variant="ghost"
              size="sm"
              onClick={loadShares}
              isLoading={isLoading}
            />
          </Tooltip>
        )}
      </Flex>
    </VStack>
  );
}
